import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import { FormPageShell } from '../../components/DataTablePanel';
import { TableIconButton } from '../../components/TableIconAction';
import {
  Button,
  Callout,
  Card,
  DateInput,
  Label,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  NumberInput,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  Select,
  Switch,
  TextInput,
  Textarea,
  TrashIcon,
  XIcon,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { ExpenseCategory } from '../../types/shipment';
import { formatMoney } from '../../utils/format';

const VAT_RATES = ['0', '6', '13', '24'];

type ContainerDraft = {
  key: string;
  id?: number;
  containerNo: string;
};

type ExpenseDraft = {
  key: string;
  categoryId: string;
  containerKey: string;
  description: string;
  invoiceNo: string;
  amount: string;
  vatRate: string;
  type: 'INVOICE' | 'CASH';
  billable: boolean;
};

type ExpenseModalState = {
  key: string | null;
  categoryId: string;
  containerKey: string;
  description: string;
  invoiceNo: string;
  amount: string;
  vatRate: string;
  type: 'INVOICE' | 'CASH';
  billable: boolean;
};

type FormState = {
  customerId: string;
  blNo: string;
  vessel: string;
  port: string;
  arrivalDate: string;
  notes: string;
  containers: ContainerDraft[];
  expenses: ExpenseDraft[];
};

function newKey(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyForm(): FormState {
  return {
    customerId: '',
    blNo: '',
    vessel: '',
    port: '',
    arrivalDate: '',
    notes: '',
    containers: [{ key: newKey('c'), containerNo: '' }],
    expenses: [],
  };
}

function emptyExpenseModal(): ExpenseModalState {
  return {
    key: null,
    categoryId: '',
    containerKey: '',
    description: '',
    invoiceNo: '',
    amount: '',
    vatRate: '24',
    type: 'INVOICE',
    billable: true,
  };
}

function calcVat(amount: string, vatRate: string): string {
  const net = Number(amount);
  const rate = Number(vatRate);
  if (!Number.isFinite(net) || !Number.isFinite(rate)) return '0.00';
  return ((net * rate) / 100).toFixed(2);
}

function calcTotals(expenses: ExpenseDraft[]) {
  let expenseNet = 0;
  let expenseVat = 0;
  let billableNet = 0;
  let billableVat = 0;
  for (const e of expenses) {
    const net = Number(e.amount) || 0;
    const vat = Number(calcVat(e.amount, e.vatRate)) || 0;
    expenseNet += net;
    expenseVat += vat;
    if (e.billable) {
      billableNet += net;
      billableVat += vat;
    }
  }
  return {
    expenseNet: expenseNet.toFixed(2),
    expenseVat: expenseVat.toFixed(2),
    expenseGross: (expenseNet + expenseVat).toFixed(2),
    billableGross: (billableNet + billableVat).toFixed(2),
  };
}

export function ShipmentFileFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const labels = t().shipmentFiles;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [fileNo, setFileNo] = useState<string | null>(null);
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [expenseModal, setExpenseModal] = useState<ExpenseModalState>(
    emptyExpenseModal,
  );
  const [expenseModalError, setExpenseModalError] = useState<string | null>(
    null,
  );

  const totals = useMemo(() => calcTotals(form.expenses), [form.expenses]);
  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(String(c.id), c.name));
    return map;
  }, [categories]);
  const containerLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    form.containers.forEach((c) =>
      map.set(c.key, c.containerNo || labels.selectContainer),
    );
    return map;
  }, [form.containers, labels.selectContainer]);

  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setLoading(true);
      try {
        const [customerResult, categoryResult] = await Promise.all([
          api.getCustomerLookup({ active: 'true' }),
          api.getExpenseCategories(true),
        ]);
        if (cancelled) return;
        setCustomers(customerResult);
        setCategories(categoryResult);

        if (isEdit && id) {
          const file = await api.getShipmentFile(Number(id));
          if (cancelled) return;
          if (file.locked) {
            navigate(`/shipment-files/${id}`, { replace: true });
            return;
          }
          setLocked(file.locked);
          setFileNo(file.fileNo);
          if (
            !customerResult.some((c) => c.id === file.customer.id)
          ) {
            setCustomers([
              ...customerResult,
              {
                id: file.customer.id,
                name: file.customer.name,
                active: true,
                creditDays: file.customer.creditDays,
              },
            ]);
          }
          const containers: ContainerDraft[] = file.containers.map((c) => ({
            key: `id:${c.id}`,
            id: c.id,
            containerNo: c.containerNo,
          }));
          setForm({
            customerId: String(file.customer.id),
            blNo: file.blNo ?? '',
            vessel: file.vessel ?? '',
            port: file.port ?? '',
            arrivalDate: file.arrivalDate
              ? file.arrivalDate.slice(0, 10)
              : '',
            notes: file.notes ?? '',
            containers:
              containers.length > 0
                ? containers
                : [{ key: newKey('c'), containerNo: '' }],
            expenses: file.expenses.map((e) => ({
              key: newKey('e'),
              categoryId: String(e.categoryId),
              containerKey: e.containerId
                ? `id:${e.containerId}`
                : '',
              description: e.description ?? '',
              invoiceNo: e.invoiceNo ?? '',
              amount: e.amount,
              vatRate: String(Number(e.vatRate)),
              type: e.type,
              billable: e.billable,
            })),
          });
          const inactiveNeeded = file.expenses
            .map((e) => e.category)
            .filter((c) => !c.active)
            .filter(
              (c, idx, arr) => arr.findIndex((x) => x.id === c.id) === idx,
            );
          if (inactiveNeeded.length > 0) {
            setCategories((prev) => {
              const ids = new Set(prev.map((c) => c.id));
              return [
                ...prev,
                ...inactiveNeeded
                  .filter((c) => !ids.has(c.id))
                  .map((c) => ({
                    id: c.id,
                    name: c.name,
                    active: c.active,
                  })),
              ];
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  function openCreateExpense() {
    setExpenseModal(emptyExpenseModal());
    setExpenseModalError(null);
    setExpenseModalOpen(true);
  }

  function openEditExpense(expense: ExpenseDraft) {
    setExpenseModal({
      key: expense.key,
      categoryId: expense.categoryId,
      containerKey: expense.containerKey,
      description: expense.description,
      invoiceNo: expense.invoiceNo,
      amount: expense.amount,
      vatRate: expense.vatRate,
      type: expense.type,
      billable: expense.billable,
    });
    setExpenseModalError(null);
    setExpenseModalOpen(true);
  }

  function closeExpenseModal() {
    setExpenseModalOpen(false);
    setExpenseModal(emptyExpenseModal());
    setExpenseModalError(null);
  }

  function saveExpenseModal(event: FormEvent) {
    event.preventDefault();
    setExpenseModalError(null);

    if (!expenseModal.categoryId) {
      setExpenseModalError(labels.requiredExpenseCategory);
      return;
    }
    if (!expenseModal.amount.trim()) {
      setExpenseModalError(labels.requiredExpenseAmount);
      return;
    }
    const amountNum = Number(expenseModal.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setExpenseModalError(labels.requiredExpenseAmount);
      return;
    }

    const next: ExpenseDraft = {
      key: expenseModal.key ?? newKey('e'),
      categoryId: expenseModal.categoryId,
      containerKey: expenseModal.containerKey,
      description: expenseModal.description.trim(),
      invoiceNo: expenseModal.invoiceNo.trim(),
      amount: amountNum.toFixed(2),
      vatRate: expenseModal.vatRate,
      type: expenseModal.type,
      billable: expenseModal.billable,
    };

    setForm((prev) => {
      const exists = prev.expenses.some((e) => e.key === next.key);
      return {
        ...prev,
        expenses: exists
          ? prev.expenses.map((e) => (e.key === next.key ? next : e))
          : [...prev.expenses, next],
      };
    });
    closeExpenseModal();
  }

  function removeExpense(key: string) {
    setForm((prev) => ({
      ...prev,
      expenses: prev.expenses.filter((e) => e.key !== key),
    }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!form.customerId) {
      setError(labels.requiredCustomer);
      return;
    }
    const containers = form.containers
      .map((c, index) => ({
        ...c,
        containerNo: c.containerNo.trim(),
        sortOrder: index,
      }))
      .filter((c) => c.containerNo);
    if (containers.length === 0) {
      setError(labels.requiredContainer);
      return;
    }

    for (const expense of form.expenses) {
      if (!expense.amount.trim() && !expense.description.trim()) continue;
      if (!expense.categoryId) {
        setError(labels.requiredExpenseCategory);
        return;
      }
      if (!expense.amount.trim()) {
        setError(labels.requiredExpenseAmount);
        return;
      }
    }

    const payload = {
      customerId: Number(form.customerId),
      blNo: form.blNo.trim() || null,
      vessel: form.vessel.trim() || null,
      port: form.port.trim() || null,
      arrivalDate: form.arrivalDate || null,
      notes: form.notes.trim() || null,
      containers: containers.map((c, index) => ({
        ...(c.id ? { id: c.id } : {}),
        containerNo: c.containerNo,
        sortOrder: index,
      })),
      expenses: form.expenses
        .filter((e) => e.amount.trim() || e.description.trim())
        .map((e, index) => ({
          categoryId: Number(e.categoryId),
          containerKey: e.containerKey || null,
          containerId: e.containerKey.startsWith('id:')
            ? Number(e.containerKey.slice(3))
            : null,
          description: e.description.trim() || undefined,
          invoiceNo: e.invoiceNo.trim() || null,
          amount: Number(e.amount).toFixed(2),
          vatRate: e.vatRate,
          type: e.type,
          billable: e.billable,
          sortOrder: index,
        })),
    };

    // Remap container keys to idx for new containers
    payload.expenses = payload.expenses.map((e) => {
      if (e.containerKey && !e.containerKey.startsWith('id:')) {
        const idx = containers.findIndex((c) => c.key === e.containerKey);
        return {
          ...e,
          containerKey: idx >= 0 ? `idx:${idx}` : null,
          containerId: null,
        };
      }
      if (e.containerKey?.startsWith('id:')) {
        const idx = containers.findIndex((c) => c.key === e.containerKey);
        return {
          ...e,
          containerKey: idx >= 0 ? `idx:${idx}` : e.containerKey,
          containerId: null,
        };
      }
      return e;
    });

    setSaving(true);
    try {
      if (isEdit && id) {
        const updated = await api.updateShipmentFile(Number(id), payload);
        navigate(`/shipment-files/${updated.id}`);
      } else {
        const created = await api.createShipmentFile(payload);
        navigate(`/shipment-files/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <FormPageShell wide>
        <LoadingSpinner message={t().common.loading} />
      </FormPageShell>
    );
  }

  return (
    <FormPageShell wide>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BackButton fallback="/shipment-files" />
          <div>
            <h1 className="text-lg font-semibold">
              {isEdit ? labels.edit : labels.new}
            </h1>
            {fileNo ? (
              <p className="text-sm text-ink-muted">
                {labels.fileNo}: {fileNo}
              </p>
            ) : (
              <p className="text-sm text-ink-muted">{labels.fileNoHint}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              navigate(isEdit && id ? `/shipment-files/${id}` : '/shipment-files')
            }
          >
            <XIcon className="h-4 w-4" />
            {t().common.cancel}
          </Button>
          <Button type="submit" form="shipment-form" loading={saving} disabled={locked}>
            <SaveIcon className="h-4 w-4" />
            {t().common.save}
          </Button>
        </div>
      </div>

      {error ? <Callout variant="danger">{error}</Callout> : null}

      <form id="shipment-form" onSubmit={handleSubmit} className="space-y-4">
        <Card className="grid gap-4 p-4 md:grid-cols-2">
          <div>
            <Label required>{labels.customer}</Label>
            <Select
              value={form.customerId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, customerId: e.target.value }))
              }
              disabled={locked}
              placeholder={labels.selectCustomer}
              options={customers.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
            />
          </div>
          <div>
            <Label>{labels.blNo}</Label>
            <TextInput
              value={form.blNo}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, blNo: e.target.value }))
              }
              disabled={locked}
              showClearButton={false}
            />
          </div>
          <div>
            <Label>{labels.vessel}</Label>
            <TextInput
              value={form.vessel}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, vessel: e.target.value }))
              }
              disabled={locked}
              showClearButton={false}
            />
          </div>
          <div>
            <Label>{labels.port}</Label>
            <TextInput
              value={form.port}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, port: e.target.value }))
              }
              disabled={locked}
              showClearButton={false}
            />
          </div>
          <div>
            <Label>{labels.arrivalDate}</Label>
            <DateInput
              value={form.arrivalDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, arrivalDate: e.target.value }))
              }
              disabled={locked}
            />
          </div>
          <div className="md:col-span-2">
            <Label>{labels.notes}</Label>
            <Textarea
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={2}
              disabled={locked}
            />
          </div>
        </Card>

        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">{labels.containersSection}</h2>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={locked}
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  containers: [
                    ...prev.containers,
                    { key: newKey('c'), containerNo: '' },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.addContainer}
            </Button>
          </div>
          <div className="space-y-2">
            {form.containers.map((container, index) => (
              <div key={container.key} className="flex gap-2">
                <TextInput
                  value={container.containerNo}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      containers: prev.containers.map((c) =>
                        c.key === container.key
                          ? { ...c, containerNo: e.target.value }
                          : c,
                      ),
                    }))
                  }
                  placeholder={labels.containerNo}
                  disabled={locked}
                  showClearButton={false}
                />
                {form.containers.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={locked}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        containers: prev.containers.filter(
                          (c) => c.key !== container.key,
                        ),
                        expenses: prev.expenses.map((e) =>
                          e.containerKey === container.key
                            ? { ...e, containerKey: '' }
                            : e,
                        ),
                      }))
                    }
                    aria-label={`${labels.removeContainer} ${index + 1}`}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </Card>

        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">{labels.expensesSection}</h2>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={locked}
              onClick={openCreateExpense}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.addExpense}
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-left text-ink-muted">
                <tr>
                  <th className="px-2 py-1">{t().common.actions}</th>
                  <th className="px-2 py-1">{labels.expenseCategory}</th>
                  <th className="px-2 py-1">{labels.expenseInvoiceNo}</th>
                  <th className="px-2 py-1">{t().financial.description}</th>
                  <th className="px-2 py-1 text-right">{labels.expenseAmount}</th>
                  <th className="px-2 py-1 text-right">{labels.vatRate}</th>
                  <th className="px-2 py-1 text-right">{labels.vatAmount}</th>
                  <th className="px-2 py-1">{t().financial.type}</th>
                  <th className="px-2 py-1">{labels.billable}</th>
                  <th className="px-2 py-1">{labels.containersSection}</th>
                </tr>
              </thead>
              <tbody>
                {form.expenses.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-2 py-4 text-center text-ink-muted"
                    >
                      —
                    </td>
                  </tr>
                ) : (
                  form.expenses.map((expense) => (
                    <tr key={expense.key} className="border-t border-line">
                      <td className="whitespace-nowrap px-2 py-1">
                        {!locked ? (
                          <div className="inline-flex items-center gap-0.5">
                            <TableIconButton
                              label={labels.editExpense}
                              onClick={() => openEditExpense(expense)}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </TableIconButton>
                            <TableIconButton
                              label={labels.removeExpense}
                              variant="muted"
                              onClick={() => removeExpense(expense.key)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </TableIconButton>
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {categoryNameById.get(expense.categoryId) || '—'}
                      </td>
                      <td className="px-2 py-1">
                        {expense.invoiceNo || '—'}
                      </td>
                      <td className="px-2 py-1">
                        {expense.description || '—'}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {formatMoney(expense.amount)}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {expense.vatRate}%
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {formatMoney(
                          calcVat(expense.amount, expense.vatRate),
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {expense.type === 'INVOICE'
                          ? t().financial.invoice
                          : t().financial.cash}
                      </td>
                      <td className="px-2 py-1">
                        {expense.billable ? t().common.yes : t().common.no}
                      </td>
                      <td className="px-2 py-1">
                        {expense.containerKey
                          ? containerLabelByKey.get(expense.containerKey) ||
                            '—'
                          : labels.allContainers}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="grid gap-2 p-4 text-sm md:grid-cols-4">
          <div>
            <div className="text-xs text-ink-faint">{labels.totalExpenses}</div>
            <div className="font-semibold tabular-nums">
              {formatMoney(totals.expenseNet)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint">{labels.totalVat}</div>
            <div className="font-semibold tabular-nums">
              {formatMoney(totals.expenseVat)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint">{labels.totalGross}</div>
            <div className="font-semibold tabular-nums">
              {formatMoney(totals.expenseGross)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint">{labels.billableTotal}</div>
            <div className="font-semibold tabular-nums">
              {formatMoney(totals.billableGross)}
            </div>
          </div>
        </Card>
      </form>

      <Modal
        isOpen={expenseModalOpen}
        onClose={closeExpenseModal}
        size="md"
      >
        <form
          onSubmit={saveExpenseModal}
          className="flex min-h-0 flex-1 flex-col"
        >
          <ModalHeader>
            <ModalTitle>
              {expenseModal.key ? labels.editExpense : labels.addExpense}
            </ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={closeExpenseModal}
              aria-label={t().common.cancel}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {expenseModalError ? (
              <Callout variant="danger">{expenseModalError}</Callout>
            ) : null}
            <div>
              <Label required>{labels.expenseCategory}</Label>
              <Select
                value={expenseModal.categoryId}
                onChange={(e) =>
                  setExpenseModal((prev) => ({
                    ...prev,
                    categoryId: e.target.value,
                  }))
                }
                placeholder={labels.selectCategory}
                options={categories.map((c) => ({
                  value: String(c.id),
                  label: c.name,
                }))}
              />
            </div>
            <div>
              <Label>{t().financial.description}</Label>
              <TextInput
                value={expenseModal.description}
                onChange={(e) =>
                  setExpenseModal((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                showClearButton={false}
              />
            </div>
            <div>
              <Label>{labels.expenseInvoiceNo}</Label>
              <TextInput
                value={expenseModal.invoiceNo}
                onChange={(e) =>
                  setExpenseModal((prev) => ({
                    ...prev,
                    invoiceNo: e.target.value,
                  }))
                }
                showClearButton={false}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label required>{labels.expenseAmount}</Label>
                <NumberInput
                  value={expenseModal.amount}
                  onChange={(e) =>
                    setExpenseModal((prev) => ({
                      ...prev,
                      amount:
                        e.target.value == null ? '' : String(e.target.value),
                    }))
                  }
                  allowDecimals
                />
              </div>
              <div>
                <Label required>{labels.vatRate}</Label>
                <Select
                  value={expenseModal.vatRate}
                  onChange={(e) =>
                    setExpenseModal((prev) => ({
                      ...prev,
                      vatRate: e.target.value,
                    }))
                  }
                  options={VAT_RATES.map((rate) => ({
                    value: rate,
                    label: `${rate}%`,
                  }))}
                />
              </div>
            </div>
            <div className="rounded-lg bg-surface-muted px-3 py-2 text-sm">
              <span className="text-ink-muted">{labels.vatAmount}: </span>
              <span className="font-medium tabular-nums">
                {formatMoney(
                  calcVat(expenseModal.amount, expenseModal.vatRate),
                )}
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>{t().financial.type}</Label>
                <Select
                  value={expenseModal.type}
                  onChange={(e) =>
                    setExpenseModal((prev) => ({
                      ...prev,
                      type: e.target.value as 'INVOICE' | 'CASH',
                    }))
                  }
                  options={[
                    { value: 'INVOICE', label: t().financial.invoice },
                    { value: 'CASH', label: t().financial.cash },
                  ]}
                />
              </div>
              <div>
                <Label>{labels.containersSection}</Label>
                <Select
                  value={expenseModal.containerKey}
                  onChange={(e) =>
                    setExpenseModal((prev) => ({
                      ...prev,
                      containerKey: e.target.value,
                    }))
                  }
                  placeholder={labels.allContainers}
                  showClearButton
                  options={form.containers.map((c) => ({
                    value: c.key,
                    label: c.containerNo || labels.selectContainer,
                  }))}
                />
              </div>
            </div>
            <Switch
              checked={expenseModal.billable}
              onChange={(e) =>
                setExpenseModal((prev) => ({
                  ...prev,
                  billable: e.target.checked,
                }))
              }
              label={labels.billable}
            />
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeExpenseModal}
            >
              <XIcon className="h-4 w-4" />
              {t().common.cancel}
            </Button>
            <Button type="submit">
              <SaveIcon className="h-4 w-4" />
              {labels.expenseModalSave}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </FormPageShell>
  );
}
